/*
 * <BeSpinner> -- React wrapper for the .be-spinner CSS component: the LibWalter
 * numeric spinner (a BTextControl sunken well + two scrollbar-style arrow
 * buttons). Ported from LibWalter Spinner.cpp; see Notes/20-spinner.md and
 * components/spinner.css for provenance.
 *
 * Press-and-hold on an arrow auto-repeats (250ms initial delay, then 150ms),
 * matching the BeOS control. The field is a native <input type=number>, so
 * ArrowUp/ArrowDown and typed entry (clamped to [min,max]) work for free; only
 * the field is keyboard-navigable, as in R5 (the arrows are not B_NAVIGABLE).
 *
 * Works controlled (value + onChange) or uncontrolled (defaultValue):
 *   <BeSpinner label="Font size:" value={n} min={1} max={999} onChange={setN} />
 *   <BeSpinner label="Count:" defaultValue={3} min={0} max={10} />
 */

import { type ChangeEvent, type CSSProperties, type LabelHTMLAttributes, useEffect, useRef, useState } from "react";
import { type BeCommon, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

/* Not BeProps<"input">: `value`, `defaultValue` and `onChange` are all NUMERIC
 * here, which is deliberately not the DOM signature -- extending the input's
 * type would union them back to strings and events. The <label> root's
 * attributes come in instead, which is where `...rest` lands anyway. */
export type BeSpinnerProps = BeCommon &
	Omit<LabelHTMLAttributes<HTMLLabelElement>, "className" | "style" | "onChange"> & {
		/** Controlled value. Omit (and use defaultValue) for uncontrolled. */
		value?: number;
		/** Initial value when uncontrolled. */
		defaultValue?: number;
		min?: number;
		max?: number;
		step?: number;
		onChange?: (value: number) => void;
		label?: string;
		disabled?: boolean;
		/** Field width in --be-px units. A content measurement, so a raw count. */
		width?: number;
	};

/** The ref lands on the numeric <input> -- see create.tsx. */
export const BeSpinner = beComponent<BeSpinnerProps, HTMLInputElement>("BeSpinner", (props, be) => {
	const {
		value,
		defaultValue,
		min = 0,
		max = 100,
		step = 1,
		onChange,
		label,
		disabled,
		width,
		// consumed by be() off `props`; named here only to keep them out of the DOM
		scale: _scale,
		className: _className,
		style: _style,
		...rest
	} = props;

	const isControlled = value !== undefined;
	const clamp = (n: number) => Math.max(min, Math.min(max, n));

	const [internal, setInternal] = useState(() => clamp(defaultValue ?? min));
	const current = isControlled ? clamp(value as number) : internal;

	// A free-typing buffer: lets the user clear / type intermediate strings
	// without the value snapping back mid-edit. null = show the numeric value.
	const [draft, setDraft] = useState<string | null>(null);
	const display = draft ?? String(current);

	// Latest committed number, read by the hold-to-repeat timer (which closes
	// over a stale render otherwise).
	const numRef = useRef(current);
	numRef.current = current;

	const apply = (n: number) => {
		numRef.current = n;
		if (!isControlled) setInternal(n);
		onChange?.(n);
	};

	const commit = (n: number) => {
		setDraft(null);
		apply(clamp(n));
	};

	const nudge = (dir: 1 | -1) => commit(numRef.current + dir * step);

	const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value;
		setDraft(raw);
		const n = Number.parseInt(raw, 10);
		// LibWalter commits each in-range edit live; out-of-range waits for blur.
		if (!Number.isNaN(n) && n >= min && n <= max) apply(n);
	};

	const onBlur = () => {
		const n = Number.parseInt(display, 10);
		commit(Number.isNaN(n) ? current : n);
	};

	// which arrow is held down -- drives the sunken "pressed" look. We can't lean
	// on CSS :active here: the pointerdown handler preventDefault()s to keep focus
	// on the field, which suppresses the mousedown that sets :active in Chromium.
	const [pressed, setPressed] = useState<1 | -1 | 0>(0);

	// hold-to-repeat: nudge once, wait 250ms, then repeat every 150ms.
	const timer = useRef<number | null>(null);
	const stop = () => {
		setPressed(0);
		if (timer.current !== null) {
			window.clearTimeout(timer.current);
			timer.current = null;
		}
	};
	useEffect(() => {
		return () => {
			if (timer.current !== null) window.clearTimeout(timer.current);
		};
	}, []);

	const start = (dir: 1 | -1) => {
		if (disabled) return;
		setPressed(dir);
		nudge(dir);
		const tick = (delay: number) => {
			timer.current = window.setTimeout(() => {
				nudge(dir);
				tick(150);
			}, delay);
		};
		tick(250);
	};

	const { ref, ...root } = be("be-spinner");
	const css = width != null ? ({ ...root.style, "--be-spinner-width": `calc(${width} * var(--be-px))` } as CSSProperties) : root.style;

	const arrow = (dir: 1 | -1) => ({
		type: "button" as const,
		className: ["be-spinner__btn", `be-spinner__btn--${dir === 1 ? "up" : "down"}`, pressed === dir && "be-spinner__btn--pressed"]
			.filter(Boolean)
			.join(" "),
		tabIndex: -1,
		disabled,
		"aria-hidden": true,
		onPointerDown: (e: React.PointerEvent) => {
			e.preventDefault(); // keep focus on the field
			start(dir);
		},
		onPointerUp: stop,
		onPointerLeave: stop,
		onPointerCancel: stop,
	});

	return (
		<label {...rest} {...root} style={css}>
			{label != null && pixelText(label, "be-spinner__label")}
			<span className="be-spinner__box">
				<input
					className="be-text-field__input be-spinner__field"
					ref={ref}
					type="number"
					value={display}
					min={min}
					max={max}
					step={step}
					disabled={disabled}
					onChange={onInputChange}
					onBlur={onBlur}
				/>
				<span className="be-spinner__buttons">
					<button {...arrow(1)}>
						<span className="be-spinner__tri" />
					</button>
					<button {...arrow(-1)}>
						<span className="be-spinner__tri" />
					</button>
				</span>
			</span>
		</label>
	);
});
